import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  getPayrollItemPreview,
  updatePayrollItemAdjustments,
} from "../services/PayrollService";
import type {
  ManualDeductionType,
  ManualEarningType,
  PayrollEarning,
  PayrollDeduction,
  PayrollItem,
  PayrollItemPreview,
} from "../types/payroll.types";
import { formatCurrency } from "utils/currency";

const EARNING_TYPES: ManualEarningType[] = ["ALLOWANCE", "BONUS", "REIMBURSEMENT", "OTHER"];
const DEDUCTION_TYPES: ManualDeductionType[] = ["PENALTY", "TAX", "OTHER"];

const EARNING_LABELS: Record<ManualEarningType, string> = {
  ALLOWANCE: "Allowance",
  BONUS: "Bonus",
  REIMBURSEMENT: "Reimbursement",
  OTHER: "Other",
};
const DEDUCTION_LABELS: Record<ManualDeductionType, string> = {
  PENALTY: "Penalty",
  TAX: "Tax",
  OTHER: "Other",
};

/** Types where the label field is required (not just optional). */
const EARNING_LABEL_REQUIRED = new Set<ManualEarningType>(["ALLOWANCE", "REIMBURSEMENT", "OTHER"]);
const DEDUCTION_LABEL_REQUIRED = new Set<ManualDeductionType>(["OTHER"]);

type EarningRow = { type: ManualEarningType; label: string; amount: string };
type DeductionRow = { type: ManualDeductionType; label: string; amount: string };

const emptyEarning = (): EarningRow => ({ type: "ALLOWANCE", label: "", amount: "" });
const emptyDeduction = (): DeductionRow => ({ type: "PENALTY", label: "", amount: "" });

function sumLines(lines: { amount?: number }[]): number {
  return lines.reduce((s, l) => s + (l.amount ?? 0), 0);
}

const PayrollItemAdjustmentsModal = ({
  isOpen,
  onClose,
  item,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  item: PayrollItem | null;
  onSaved: () => void;
}) => {
  const [earnings, setEarnings] = useState<EarningRow[]>([]);
  const [deductions, setDeductions] = useState<DeductionRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState<PayrollItemPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !item) {
      setPreview(null);
      return;
    }

    setEarnings(
      (item.earnings ?? [])
        .filter((e) => e.isManual)
        .map((e) => ({
          type: (e.type ?? "BONUS") as ManualEarningType,
          label: e.label ?? "",
          amount: e.amount != null ? String(e.amount) : "",
        })),
    );
    setDeductions(
      (item.deductions ?? [])
        .filter((d) => d.isManual)
        .map((d) => ({
          type: (d.type ?? "PENALTY") as ManualDeductionType,
          label: d.label ?? "",
          amount: d.amount != null ? String(d.amount) : "",
        })),
    );

    let cancelled = false;
    setPreviewLoading(true);
    getPayrollItemPreview(item.id)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) setPreview(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, item]);

  const systemEarnings: PayrollEarning[] = useMemo(
    () => (preview?.earnings ?? []).filter((e) => !e.isManual),
    [preview],
  );
  const systemDeductions: PayrollDeduction[] = useMemo(
    () => (preview?.deductions ?? []).filter((d) => !d.isManual),
    [preview],
  );

  const liveTotals = useMemo(() => {
    const manualEarningsSum = earnings.reduce(
      (s, e) => s + (parseFloat(e.amount) || 0),
      0,
    );
    const manualDeductionsSum = deductions.reduce(
      (s, d) => s + (parseFloat(d.amount) || 0),
      0,
    );
    const sysEarnings = sumLines(systemEarnings);
    const sysDeductions = sumLines(systemDeductions);
    const gross = sysEarnings + manualEarningsSum;
    const totalDeductions = sysDeductions + manualDeductionsSum;
    return { gross, totalDeductions, net: gross - totalDeductions };
  }, [earnings, deductions, systemEarnings, systemDeductions]);

  const updateEarning = (i: number, patch: Partial<EarningRow>) =>
    setEarnings((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const updateDeduction = (i: number, patch: Partial<DeductionRow>) =>
    setDeductions((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const handleSubmit = async () => {
    if (!item) return;
    for (const e of earnings) {
      if (EARNING_LABEL_REQUIRED.has(e.type) && !e.label.trim()) {
        toast.error(`${EARNING_LABELS[e.type]} requires a label`);
        return;
      }
      if (!e.amount || isNaN(Number(e.amount)) || Number(e.amount) <= 0) {
        toast.error("Each earning must have a positive amount");
        return;
      }
    }
    for (const d of deductions) {
      if (DEDUCTION_LABEL_REQUIRED.has(d.type) && !d.label.trim()) {
        toast.error(`${DEDUCTION_LABELS[d.type]} requires a label`);
        return;
      }
      if (!d.amount || isNaN(Number(d.amount)) || Number(d.amount) <= 0) {
        toast.error("Each deduction must have a positive amount");
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await updatePayrollItemAdjustments(item.id, {
        earnings: earnings.map((e) => ({
          type: e.type,
          label: e.label.trim(),
          amount: Number(e.amount),
        })),
        deductions: deductions.map((d) => ({
          type: d.type,
          label: d.label.trim(),
          amount: Number(d.amount),
        })),
      });
      if (res.success) {
        toast.success(res.message || "Adjustments saved");
        onSaved();
        onClose();
      } else {
        toast.error(res.message || "Could not save adjustments");
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message;
      toast.error(msg || "Could not save adjustments");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="xl">
      <ModalHeader toggle={onClose}>
        Manual adjustments
        {item && (
          <small className="text-muted ms-2 fw-normal fs-13">
            Item #{item.id}
          </small>
        )}
      </ModalHeader>
      <ModalBody>
        <div className="row g-4">
          {/* Left — system breakdown (read-only) */}
          <div className="col-md-4">
            <h6 className="text-muted text-uppercase fs-11 mb-3">
              System lines
            </h6>
            {previewLoading ? (
              <div className="text-center py-3">
                <Spinner size="sm" />
              </div>
            ) : (
              <>
                {systemEarnings.length > 0 && (
                  <div className="mb-3">
                    <p className="text-muted small mb-1 fw-medium">Earnings</p>
                    {systemEarnings.map((e, i) => (
                      <div
                        key={i}
                        className="d-flex justify-content-between small py-1 border-bottom"
                      >
                        <span className="text-body">{e.label ?? e.type}</span>
                        <span className="tabular-nums">
                          {formatCurrency(e.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {systemDeductions.length > 0 && (
                  <div className="mb-3">
                    <p className="text-muted small mb-1 fw-medium">
                      Deductions
                    </p>
                    {systemDeductions.map((d, i) => (
                      <div
                        key={i}
                        className="d-flex justify-content-between small py-1 border-bottom"
                      >
                        <span className="text-body">{d.label ?? d.type}</span>
                        <span className="tabular-nums text-danger">
                          -{formatCurrency(d.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {!systemEarnings.length && !systemDeductions.length && (
                  <p className="text-muted small">No system lines yet.</p>
                )}

                {/* Live totals */}
                <div className="mt-3 p-2 bg-light rounded">
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted">Gross</span>
                    <span className="tabular-nums fw-medium">
                      {formatCurrency(liveTotals.gross)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted">Deductions</span>
                    <span className="tabular-nums text-danger">
                      -{formatCurrency(liveTotals.totalDeductions)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between small fw-bold border-top pt-1 mt-1">
                    <span>Net pay</span>
                    <span className="tabular-nums text-success">
                      {formatCurrency(liveTotals.net)}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Right — manual adjustments form */}
          <div className="col-md-8">
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Manual earnings</h6>
                <Button
                  size="sm"
                  color="soft-success"
                  onClick={() => setEarnings((p) => [...p, emptyEarning()])}
                >
                  <i className="ri-add-line me-1" />
                  Add
                </Button>
              </div>
              {earnings.length === 0 ? (
                <p className="text-muted small mb-0">None added.</p>
              ) : (
                <Table size="sm" bordered className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 120 }}>Type</th>
                      <th>Label</th>
                      <th style={{ width: 110 }}>Amount</th>
                      <th style={{ width: 36 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.map((row, i) => (
                      <tr key={i}>
                        <td>
                          <Input
                            type="select"
                            bsSize="sm"
                            value={row.type}
                            onChange={(e) =>
                              updateEarning(i, {
                                type: e.target.value as ManualEarningType,
                              })
                            }
                          >
                            {EARNING_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {EARNING_LABELS[t]}
                              </option>
                            ))}
                          </Input>
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            placeholder="e.g. Sales incentive"
                            value={row.label}
                            onChange={(e) =>
                              updateEarning(i, { label: e.target.value })
                            }
                          />
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            type="number"
                            min={0}
                            step="0.01"
                            placeholder="0.00"
                            value={row.amount}
                            onChange={(e) =>
                              updateEarning(i, { amount: e.target.value })
                            }
                          />
                        </td>
                        <td className="text-center">
                          <Button
                            size="sm"
                            color="soft-danger"
                            onClick={() =>
                              setEarnings((p) => p.filter((_, idx) => idx !== i))
                            }
                          >
                            <i className="ri-delete-bin-line" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>

            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Manual deductions</h6>
                <Button
                  size="sm"
                  color="soft-danger"
                  onClick={() => setDeductions((p) => [...p, emptyDeduction()])}
                >
                  <i className="ri-add-line me-1" />
                  Add
                </Button>
              </div>
              {deductions.length === 0 ? (
                <p className="text-muted small mb-0">None added.</p>
              ) : (
                <Table size="sm" bordered className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 120 }}>Type</th>
                      <th>Label</th>
                      <th style={{ width: 110 }}>Amount</th>
                      <th style={{ width: 36 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {deductions.map((row, i) => (
                      <tr key={i}>
                        <td>
                          <Input
                            type="select"
                            bsSize="sm"
                            value={row.type}
                            onChange={(e) =>
                              updateDeduction(i, {
                                type: e.target.value as ManualDeductionType,
                              })
                            }
                          >
                            {DEDUCTION_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {DEDUCTION_LABELS[t]}
                              </option>
                            ))}
                          </Input>
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            placeholder="e.g. Late arrival fine"
                            value={row.label}
                            onChange={(e) =>
                              updateDeduction(i, { label: e.target.value })
                            }
                          />
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            type="number"
                            min={0}
                            step="0.01"
                            placeholder="0.00"
                            value={row.amount}
                            onChange={(e) =>
                              updateDeduction(i, { amount: e.target.value })
                            }
                          />
                        </td>
                        <td className="text-center">
                          <Button
                            size="sm"
                            color="soft-danger"
                            onClick={() =>
                              setDeductions((p) =>
                                p.filter((_, idx) => idx !== i),
                              )
                            }
                          >
                            <i className="ri-delete-bin-line" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button color="primary" disabled={submitting} onClick={handleSubmit}>
          {submitting ? <Spinner size="sm" className="me-1" /> : null}
          Save adjustments
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PayrollItemAdjustmentsModal;